import * as yup from 'yup'
import { useForm } from 'vee-validate'
import { useValidationSchemas } from '~/composables/useValidationSchemas'

import type { ICreateWarehouseman } from '../types'

export const useWareHouseMan = (id?: string) => {
  const { t } = useI18n()

  const regexDigits = /^\d+$/

  const {
    emailSchema,
    passwordSchema,
    nameSchema,
    surnameSchema,
    middlenameSchema,
    phoneSchema,
  } = useValidationSchemas()

  const initialValues = readonly<ICreateWarehouseman>({
    user: {
      email: '',
      name: '',
      surname: '',
      middlename: '',
      phone: '',
      is_active: '',
      password: '',
    },
    warehouseman: {
      full_name: '',
      short_name: '',
      ceo_name: '',
      ceo_position: '',
      founded_on_basis: '',
      inn: '',
      kpp: '',
      ogrn: '',
      ogrn_date: '',
      tax_authority: '',
      bank_bik: '',
      bank_name: '',
      bank_corr_account: '',
      bank_account: '',
      legal_address: '',
      contact_person_name: '',
      contact_person_phone: '',
    },
  })

  const schema = computed(() => {
    const baseSchema = yup.object({
      warehouseman: yup.object({
        full_name: yup.string().required(t('forms.errors.fieldRequired')),
        short_name: yup.string().required(t('forms.errors.fieldRequired')),
        ceo_name: yup.string().required(t('forms.errors.fieldRequired')),
        ceo_position: yup.string().required(t('forms.errors.fieldRequired')),
        founded_on_basis: yup
          .string()
          .required(t('forms.errors.fieldRequired')),
        inn: yup
          .string()
          .max(10, t('forms.errors.max-length', { count: 10 }))
          .matches(regexDigits, t('forms.errors.only_digits'))
          .required(t('forms.errors.fieldRequired')),
        kpp: yup
          .string()
          .max(9, t('forms.errors.max-length', { count: 9 }))
          .matches(regexDigits, t('forms.errors.only_digits'))
          .required(t('forms.errors.fieldRequired')),
        ogrn: yup
          .string()
          .max(13, t('forms.errors.max-length', { count: 13 }))
          .matches(regexDigits, t('forms.errors.only_digits'))
          .required(t('forms.errors.fieldRequired')),
        ogrn_date: yup.string().required(t('forms.errors.fieldRequired')),
        tax_authority: yup.string().required(t('forms.errors.fieldRequired')),
        bank_bik: yup
          .string()
          .max(9, t('forms.errors.max-length', { count: 9 }))
          .matches(regexDigits, t('forms.errors.only_digits'))
          .required(t('forms.errors.fieldRequired')),
        bank_name: yup.string().required(t('forms.errors.fieldRequired')),
        bank_corr_account: yup
          .string()
          .max(20, t('forms.errors.max-length', { count: 20 }))
          .matches(regexDigits, t('forms.errors.only_digits'))
          .required(t('forms.errors.fieldRequired')),
        bank_account: yup
          .string()
          .max(20, t('forms.errors.max-length', { count: 20 }))
          .matches(regexDigits, t('forms.errors.only_digits'))
          .required(t('forms.errors.fieldRequired')),
        legal_address: yup.string().required(t('forms.errors.fieldRequired')),
        contact_person_name: yup
          .string()
          .required(t('forms.errors.fieldRequired')),
        contact_person_phone: phoneSchema,
      }),
    })

    if (!id) {
      return baseSchema.concat(
        yup.object({
          user: yup.object({
            email: emailSchema,
            password: passwordSchema,
            name: nameSchema,
            surname: surnameSchema,
            middlename: middlenameSchema,
            phone: phoneSchema,
            is_active: yup
              .string()
              .min(5, t('forms.errors.is_active', { count: 5 }))
              .required(t('forms.errors.fieldRequired')),
          }),
        })
      )
    }

    return baseSchema
  })

  const { resetForm, handleSubmit, defineField, meta, errors } =
    useForm<ICreateWarehouseman>({
      validationSchema: computed(() => toTypedSchema(schema.value)),
      initialValues: { ...initialValues },
    })

  const formChanged = computed(() => meta.value.dirty)

  const [email] = defineField('user.email')
  const [name] = defineField('user.name')
  const [surname] = defineField('user.surname')
  const [middlename] = defineField('user.middlename')
  const [phone] = defineField('user.phone')
  const [is_active] = defineField('user.is_active')
  const [password] = defineField('user.password')
  const [full_name] = defineField('warehouseman.full_name')
  const [short_name] = defineField('warehouseman.short_name')
  const [ceo_name] = defineField('warehouseman.ceo_name')
  const [ceo_position] = defineField('warehouseman.ceo_position')
  const [founded_on_basis] = defineField('warehouseman.founded_on_basis')
  const [inn] = defineField('warehouseman.inn')
  const [kpp] = defineField('warehouseman.kpp')
  const [ogrn] = defineField('warehouseman.ogrn')
  const [ogrn_date] = defineField('warehouseman.ogrn_date')
  const [tax_authority] = defineField('warehouseman.tax_authority')
  const [bank_bik] = defineField('warehouseman.bank_bik')
  const [bank_name] = defineField('warehouseman.bank_name')
  const [bank_corr_account] = defineField('warehouseman.bank_corr_account')
  const [bank_account] = defineField('warehouseman.bank_account')
  const [legal_address] = defineField('warehouseman.legal_address')
  const [contact_person_name] = defineField('warehouseman.contact_person_name')
  const [contact_person_phone] = defineField(
    'warehouseman.contact_person_phone'
  )

  const formData = reactive({
    user: {
      email,
      name,
      surname,
      middlename,
      phone,
      is_active,
      password,
    },
    warehouseman: {
      full_name,
      short_name,
      ceo_name,
      ceo_position,
      founded_on_basis,
      inn,
      kpp,
      ogrn,
      ogrn_date,
      tax_authority,
      bank_bik,
      bank_name,
      bank_corr_account,
      bank_account,
      legal_address,
      contact_person_name,
      contact_person_phone,
    },
  })

  return {
    formChanged,
    formData,
    errors,
    resetForm,
    handleSubmit,
    schema,
  }
}
